import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { FormLabel } from '@mui/material';
import { CoreDataTextField } from '../../../../components/core/textFieldWithCoreData';
import _ from 'lodash';
import moment from 'moment';
import { ControlDiagnosis, ControlInvestigations, ControlTreatment } from '../../../../controls/agyle/diagnosis';
import Typography from '@mui/material/Typography';
import { TextFieldDebounce } from '../../../../controls/agyle/textFieldDebounce';
import { useStyles } from '../../../../controls/agyle/triageControls';
import { useDispatch, useSelector } from 'react-redux';
import { user } from '../../../../store/reducers/user';
import { ChiefOfComplaint, Safeguarding } from '../../../../controls/agyle/ecds';
import uuidv4 from 'uuid/v4';
import Formatter from '../../../../util/formatter';
import { chiefComplaint, getEcdsVersion } from '../../../../store/reducers/dicts';
import { calculateArrivalDate } from '../../../../util/patientUtil';
import InputAdornment from '@mui/material/InputAdornment';
import useOpenState from '../../../../hooks/useOpenState';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleSelectField from '../../../../components/ui/selector/agyleSelectField';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import ObservationsComparison from '../../../../components/observation/observationsComparison';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import { PATIENT_AGE_TYPE } from '../../../../util/patientContextUtil';
import { getConfig } from '../../../../util/configUtil';
import { openPatientForm } from '../../../../store/reducers/patientContext';
import { consultantDictionary } from '../../../../controls/agyle/clinician/clinitianUtils';
import ControlConsultantComponent from '../../../../controls/agyle/clinician/controlConsultantComponent';

export const diagnosisBase = () => {
    return {
        primary: [],
        secondary: [],
        workingDiagnosis: '',
    };
};

const unitOptions = [
    { id: 'kg', value: 'kg', name: 'Metric' },
    { id: 'st', value: 'st', name: 'Imperial Stones' },
    { id: 'lb', value: 'lb', name: 'Imperial Pounds' },
];

const Clerking = (props) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);
    const dispatch = useDispatch();

    const dictChiefComplaint = useSelector(chiefComplaint);
    const version = dispatch(getEcdsVersion(props.patient?.admission?.startDate));

    const [loadingForm, setLoadingForm] = useState(true);

    const [expanded, setExpanded] = React.useState({
        obs: false,
        information: false,
        safeguarding: false,
        investigation: false,
        managePlan: false,
        diagnosis: false,
        additional: false,
        frailty: false,
    });

    // Uniq id for CoreDS
    const [presentingComplaintId] = useState(() => uuidv4() + '');
    const [historicPresentingComplaintId] = useState(() => uuidv4() + '');
    const [pastMedicalHistoryId] = useState(() => uuidv4() + '');
    const [medicationsId] = useState(() => uuidv4() + '');
    const [socialHistoryId] = useState(() => uuidv4() + '');
    const [allergiesAndReactionsId] = useState(() => uuidv4() + '');
    const [preHospitalAmbulanceId] = useState(() => uuidv4() + '');
    const [examinationFieldId] = useState(() => uuidv4() + '');
    const [clerkingReviewFieldId] = useState(() => uuidv4() + '');
    const [diagnosisInformationId] = useState(() => uuidv4() + '');
    const [managementPlanId] = useState(() => uuidv4() + '');
    const [investingationsResultsId] = useState(() => uuidv4() + '');

    const [diagnosis, setDiagnosis] = React.useState(props.patient.diagnosis ? props.patient.diagnosis : diagnosisBase());

    const [medicationReview, setMedicationReview] = useState(null);
    const [medicationReviewAdjustment, setMedicationReviewAdjustment] = useState(null);

    const clerkingBase = {
        who: null,
        whoUserName: null,
        when: null,
        clinician: null,
        patientWeight: '',
        patientWeightUnits: 'kg',
        presentingComplaint: '',
        presentingComplaintAuthor: '',
        historicPresentingComplaint: '',
        historicPresentingComplaintAuthor: '',
        pastMedicalHistory: null,
        pastMedicalHistoryAuthor: null,
        allergies: null,
        allergiesAuthor: null,
        medications: null,
        medicationsAuthor: null,
        examination: '',
        clerkingReview: '',
        workingDiagnosis: '',
        investigationsDetails: '',
        treatmentAndManagementPlanDetails: '',
        additionalInformation: '',
        socialHistory: null,
        diagnosis: [],
        investigations: [],
        treatmentAndManagementPlan: [],
        chiefComplaint: null,
        chiefComplaintAuthor: '',
        safeguardingConcerns: false,
        safeguardingConcernsDetails: '',
        safeguarding: [],
        childProtectionConcerns: false,
        childConcernsDetails: '',
        status: 'new',
        preHospitalAmbulanceInfo: '',
        medicationReview: '',
        medicationReviewAdjustment: '',
    };

    // Configs
    const dateFormat = getConfig('defaultDateTimeFormat');
    const disableTriageSafeguardingConcernsAutoCheck = getConfig('disableTriageSafeguardingConcernsAutoCheck', false);
    const showDifferentialDiagnosisSeeAndTreat = getConfig('showDifferentialDiagnosisSeeAndTreat', false);
    const clerkingGuideWithCardio = getConfig('clerkingGuideWithCardio', false);
    const allergiesMandatory = getConfig('allergiesMandatory', false);
    const copyObsButtonsAvailable = getConfig('copyObsButtonsAvailable', false);
    const clerkingReview = getConfig('clerkingReview', false);
    const frailtyAgyleMiniForm = getConfig('frailtyAgyleMiniForm', false);
    const hideECDSInClerking = getConfig('hideECDSInClerking', false);
    const disableChiefComplaint = getConfig('disableChiefComplaint', false);

    const { isSmall } = props;

    const accordionChangeHandler = (accordion) => (ev) => {
        setExpanded((prev) => ({
            ...prev,
            [accordion]: !prev[accordion],
        }));
    };

    const getClerkingBase = () => {
        let createdForm = _.merge({}, clerkingBase, props.patient.clerking);
        let chief = null;

        if (props.patient?.triage?.chiefComplaint) {
            createdForm.chiefComplaint = props.patient?.triage?.chiefComplaint;
            createdForm.chiefComplaintAuthor = props.patient?.triage?.chiefComplaintAuthor;
        } else if (
            (props.patient?.admission?.initialAEComplaintDescription || props.patient?.admission?.initialAEComplaintSnomedCode) &&
            !props.patient?.triage
        ) {
            const description = props.patient?.admission?.initialAEComplaintDescription;
            const code = props.patient?.admission?.initialAEComplaintSnomedCode;

            _.forEach(dictChiefComplaint, (el) => {
                if (
                    el.ecdsVersion === version &&
                    ((code && _.startsWith(el?.snomedCode, code)) || _.startsWith(el?.__description, description))
                ) {
                    chief = el;
                }
            });
        }

        if (chief) {
            createdForm.chiefComplaint = chief;
            createdForm.chiefComplaintAuthor = 'PAS @ ' + moment().set('second', 0).format(dateFormat);
        }

        if (_.isEmpty(createdForm.workingDiagnosis) && props.patient?.diagnosis?.workingDiagnosis !== '') {
            createdForm.workingDiagnosis = props.patient?.diagnosis?.workingDiagnosis;
            setDiagnosis((prev) => ({ ...prev, workingDiagnosis: '' }));
        }

        if (props.patient?.studies?.mainScreenSafeguarding) {
            if (!disableTriageSafeguardingConcernsAutoCheck) {
                createdForm.safeguardingConcerns = true;
                createdForm.safeguardingConcernsAuthor =
                    'Safeguarding @ ' + moment(props.patient?.studies?.paedSafeguarding?.date).format(dateFormat);
            }
            createdForm.safeguarding = props.patient.studies.mainScreenSafeguarding;
        }

        if (props.patient?.diagnosis) {
            createdForm.diagnosis = props.patient?.diagnosis.primary;
        }

        if (showDifferentialDiagnosisSeeAndTreat && _.isEmpty(createdForm.workingDiagnosis)) {
            createdForm.workingDiagnosis = props.patient?.studies?.seeAndTreat?.workingDiagnosis || '';
        }

        if (createdForm.historicPresentingComplaint === '' && props.patient?.metadata?.historyOfPresentingComplaint) {
            createdForm.historicPresentingComplaint = props.patient?.metadata?.historyOfPresentingComplaint;
        }

        createdForm.who = tester;
        createdForm.whoUserName = userObject.userName;
        createdForm.when = props.patient.clerking?.when ? props.patient.clerking.when : moment().set('second', 0).toISOString();

        setLoadingForm(false);

        return createdForm;
    };

    const setAuthor = (clerking) => {
        let clerkingTmp = {
            ...clerking,
            patientWeightUnits: clerking?.patientWeightUnits ? clerking.patientWeightUnits : 'kg',
        };
        clerkingTmp.who = tester;
        clerkingTmp.whoUserName = userObject.userName;
        clerkingTmp.when = props.patient.clerking?.when ? props.patient.clerking.when : moment().set('second', 0).toISOString();
        if (_.isEmpty(clerkingTmp.workingDiagnosis) && props.patient?.diagnosis?.workingDiagnosis !== '') {
            clerkingTmp.workingDiagnosis = props.patient?.diagnosis?.workingDiagnosis;
            setDiagnosis((prev) => ({ ...prev, workingDiagnosis: '' }));
        }

        setLoadingForm(false);

        return clerkingTmp;
    };

    const [isOpenObsDialog, openObsDialog, closeObsDialog] = useOpenState(false);
    const [chosenObs, setChosenObs] = useState([]);

    const copyLastObs = () => {
        const lastObs = Formatter.formatObservationToText(props.patient?.patient?.lastObservation);
        const clone = _.cloneDeep(clerkingObject);
        clone.examination = clone.examination !== '' ? clone.examination + '\n' + lastObs : lastObs;
        setClerkingObject(clone);
    };

    const getClerking = () => {
        if (props.patient.clerking) {
            return setAuthor(props.patient.clerking);
        } else if (props.patient.triage) {
            let f = getClerkingBase();
            Object.keys(f).forEach((key) => {
                if (
                    props.patient.triage.hasOwnProperty(key) &&
                    key !== 'who' &&
                    key !== 'whoUserName' &&
                    key !== 'when' &&
                    key !== 'historicPresentingComplaint' &&
                    key !== 'historicPresentingComplaintAuthor'
                ) {
                    f[key] = props.patient.triage[key];
                }
            });

            return f;
        } else {
            return getClerkingBase();
        }
    };

    const lazyChangeFunction = (field, event) => {
        setClerkingObject((pr) => ({
            ...pr,
            [field]: event.target.value,
            [field + 'Author']: pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
        }));
    };

    const [clerkingObject, setClerkingObject] = React.useState(getClerking);
    const [triageObject, setTriageObject] = React.useState(props.patient.triage ? props.patient.triage : null);

    const [init, setInit] = React.useState(true);
    const nutritionInit = useRef(false);

    useEffect(() => {
        if (diagnosis && clerkingObject) {
            if (init || nutritionInit.current) {
                nutritionInit.current = false;
                setInit(false);
            } else {
                props.saveChange(true);
            }
        }
    }, [diagnosis, clerkingObject]);

    useEffect(() => {
        if (
            !props.patient?.clerking &&
            clerkingObject.patientWeight === '' &&
            props.lastNutrition &&
            moment(props.lastNutrition.date).isAfter(props.patient?.admission?.startDate)
        ) {
            nutritionInit.current = true;
            setClerkingObject((prev) => ({
                ...prev,
                patientWeight: props.lastNutrition.weight,
            }));
        }
    }, [props.lastNutrition]);

    useEffect(() => {
        if (diagnosis) props.saveDiagnosis(diagnosis);
    }, [diagnosis]);

    useEffect(() => {
        if (clerkingObject) props.saveClearking(clerkingObject);
    }, [clerkingObject]);

    useEffect(() => {
        if (triageObject) props.saveTriage(triageObject);
    }, [triageObject]);

    const handleChangeTextField = (event) => {
        const { name, value } = event.target;
        setClerkingObject((pr) => ({
            ...pr,
            [name]: value,
        }));
    };

    const handleAddGuide = () => {
        const text = `General Review: \nAMTS: ${clerkingGuideWithCardio ? '\nCardio:' : ''}
CVS:
Resp:
Abdo:
Neuro:
MSK/Skin:`;

        setClerkingObject((pr) => ({
            ...pr,
            examination: text,
        }));
    };

    return (
        clerkingObject && (
            <Grid container spacing={2} alignItems="center">
                <Grid container direction="row" spacing={2} sx={{ mt: 2, pl: 2 }}>
                    <Grid item xs={isSmall ? 12 : 6}>
                        <ControlConsultantComponent
                            error={props.isError && !clerkingObject.clinician}
                            agyleRefresh
                            label={t('Clerking Clinician')}
                            value={clerkingObject.clinician}
                            disabled={!props.canEditClerking}
                            findUser={userObject?.userName}
                            loading={loadingForm}
                            onChange={(value, initChange) => {
                                setInit(initChange);

                                setClerkingObject((pr) => ({
                                    ...pr,
                                    clinician: value,
                                }));
                            }}
                            allowedConsultants={consultantDictionary.triage}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={isSmall ? 12 : 6}>
                        <AgyleDatePicker
                            label={t('Date and Time of Clerking')}
                            value={moment(clerkingObject.when)}
                            error={!clerkingObject.when && props.isError}
                            disableToolbar
                            minDate={moment(calculateArrivalDate(props.patient))}
                            maxDate={moment()}
                            disabled={!props.canEditClerking}
                            onError={props.pickerError}
                            onChange={(value) => {
                                if (value) {
                                    setClerkingObject((pr) => ({ ...pr, when: value.toISOString() }));
                                } else {
                                    setClerkingObject((pr) => ({ ...pr, when: null }));
                                }
                            }}
                            InputProps={{ classes: { root: classes.inputLabel } }}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>
                </Grid>

                {props.fullFormMode && (
                    <>
                        <Grid item xs={12}>
                            <ChiefOfComplaint
                                value={clerkingObject.chiefComplaint}
                                label={t('ECDS Chief Complaint')}
                                disabled={disableChiefComplaint}
                                agyleRefresh
                                onChange={(value) => {
                                    setClerkingObject((pr) => ({
                                        ...pr,
                                        chiefComplaint: value,
                                        chiefComplaintAuthor: pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                    }));
                                }}
                                admitDate={props.patient?.admission?.startDate}
                                noBorder={false}
                                withNewBackground
                                size="large"
                                error={!clerkingObject.chiefComplaint}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <AgyleAccordion
                                label={t('Additional Information')}
                                expanded={expanded.additional}
                                onChange={accordionChangeHandler('additional')}
                                formBackground
                            >
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12}>
                                        <CoreDataTextField
                                            patient={props.patient.patient.__id}
                                            path="core.patient.preHospitalAmbulanceInfo"
                                            instanceId={preHospitalAmbulanceId}
                                            label={t('Pre-Hospital / Ambulance')}
                                            variant="outlined"
                                            multiline
                                            agyleRefresh
                                            disabled={!props.canEditClerking}
                                            value={clerkingObject.preHospitalAmbulanceInfo}
                                            onChange={(e) => lazyChangeFunction('preHospitalAmbulanceInfo', e)}
                                            showCoreValue
                                            height={70}
                                            autoFillEmpty={false}
                                            hideHistory={props.pastAttendances}
                                            noBorder={false}
                                            withNewBackground
                                            size="large"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <CoreDataTextField
                                            patient={props.patient.patient.__id}
                                            label={t('Triage History of Presenting Complaint')}
                                            path="core.patient.historicPresentingComplaint"
                                            variant="outlined"
                                            multiline
                                            agyleRefresh
                                            disabled={true}
                                            value={props.patient.triage?.historicPresentingComplaint}
                                            height={70}
                                            hideHistory={props.pastAttendances}
                                            noBorder={false}
                                            withNewBackground
                                            size="large"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ControlInvestigations
                                            label={t('Rapid Assessment Investigations')}
                                            value={props.patient.seen != null ? props.patient.seen.investigations : null}
                                            disableEditTable
                                            disabled
                                            color={'secondary'}
                                            disableCloseOnSelect
                                            showAll
                                            agyleRefresh
                                            admitDate={props.patient?.admission?.startDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ControlTreatment
                                            label={t('Rapid Assessment Treatment')}
                                            value={props.patient.seen != null ? props.patient.seen.treatments : null}
                                            disableEditTable
                                            disabled
                                            color={'secondary'}
                                            disableCloseOnSelect
                                            showAll
                                            agyleRefresh
                                            admitDate={props.patient?.admission?.startDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <CoreDataTextField
                                            patient={props.patient.patient.__id}
                                            label={t('Rapid Assessment Summary')}
                                            variant="outlined"
                                            multiline
                                            disabled={true}
                                            value={props.patient.seen?.additionalInformation}
                                            showCoreValue={false}
                                            height={70}
                                            agyleRefresh
                                            hideHistory={props.pastAttendances}
                                            noBorder={false}
                                            withNewBackground
                                            size="large"
                                        />
                                    </Grid>
                                </Grid>
                            </AgyleAccordion>
                        </Grid>

                        <Grid item xs={12}>
                            <CoreDataTextField
                                patient={props.patient.patient.__id}
                                path="core.patient.presentingComplaint"
                                instanceId={presentingComplaintId}
                                label={t('Presenting Complaint')}
                                variant="outlined"
                                multiline
                                agyleRefresh
                                disabled={!props.canEditClerking}
                                value={clerkingObject.presentingComplaint}
                                onChange={(e) => lazyChangeFunction('presentingComplaint', e)}
                                showCoreValue
                                height={70}
                                hideHistory={props.pastAttendances}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <CoreDataTextField
                                patient={props.patient.patient.__id}
                                path="core.patient.historicPresentingComplaint"
                                instanceId={historicPresentingComplaintId}
                                label={t('History of Presenting Complaint')}
                                variant="outlined"
                                multiline
                                agyleRefresh
                                patientTriageHistory={props.patient.triage?.historicPresentingComplaint}
                                disabled={!props.canEditClerking}
                                value={clerkingObject.historicPresentingComplaint}
                                onChange={(e) => lazyChangeFunction('historicPresentingComplaint', e)}
                                height={70}
                                showHistoryAsDialog
                                hideHistory={props.pastAttendances}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid container spacing={2} sx={{ mt: 1, pl: 2 }}>
                            {props.patientType === PATIENT_AGE_TYPE.PAEDS && (
                                <Fragment>
                                    <Grid item xs={12} sm={6}>
                                        <AgyleTextField
                                            disabled={!props.canEditClerking}
                                            style={{ width: '100%' }}
                                            label={t('Patient Weight')}
                                            type="number"
                                            fullWidth
                                            onChange={(event) => {
                                                const { value } = event.target;
                                                if (!_.isNaN(value)) {
                                                    setClerkingObject((pr) => ({
                                                        ...pr,
                                                        patientWeight: value,
                                                    }));
                                                }
                                            }}
                                            value={clerkingObject.patientWeight}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            endAdornment={
                                                <InputAdornment position="end">{clerkingObject.patientWeightUnits}</InputAdornment>
                                            }
                                            noBorder={false}
                                            withNewBackground
                                            size="large"
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <AgyleSelectField
                                            label={t('Unit')}
                                            style={{ height: 54 }}
                                            onChange={(value) => {
                                                setClerkingObject((pr) => ({
                                                    ...pr,
                                                    patientWeightUnits: value,
                                                }));
                                            }}
                                            withBackground
                                            options={unitOptions}
                                            value={clerkingObject?.patientWeightUnits || 'kg'}
                                            withBorder
                                            withNewBackground
                                            size="medium"
                                        />
                                    </Grid>
                                </Fragment>
                            )}
                            <Grid item xs={12}>
                                <CoreDataTextField
                                    disabled={!props.canEditClerking}
                                    patient={props.patient.patient.__id}
                                    path="core.patient.pastMedicalHistory"
                                    instanceId={pastMedicalHistoryId}
                                    label={t('Past Medical History')}
                                    variant="outlined"
                                    agyleRefresh
                                    fullWidth
                                    value={clerkingObject.pastMedicalHistory}
                                    onChange={(e) => lazyChangeFunction('pastMedicalHistory', e)}
                                    showCoreValue
                                    multiline
                                    height={70}
                                    hideHistory={props.pastAttendances}
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CoreDataTextField
                                    disabled={!props.canEditClerking}
                                    patient={props.patient.patient.__id}
                                    path="core.patient.medications"
                                    instanceId={medicationsId}
                                    label={t('Medications')}
                                    variant="outlined"
                                    value={clerkingObject.medications}
                                    onChange={(e) => lazyChangeFunction('medications', e)}
                                    showCoreValue
                                    multiline
                                    agyleRefresh
                                    fullWidth
                                    height={70}
                                    hidePreview
                                    hideHistory={props.pastAttendances}
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CoreDataTextField
                                    disabled={!props.canEditClerking}
                                    patient={props.patient.patient.__id}
                                    path="core.patient.allergies"
                                    label={t('Allergies and reactions')}
                                    instanceId={allergiesAndReactionsId}
                                    variant="outlined"
                                    value={clerkingObject.allergies}
                                    onChange={(e) => lazyChangeFunction('allergies', e)}
                                    showCoreValue
                                    multiline
                                    agyleRefresh
                                    fullWidth
                                    height={70}
                                    error={allergiesMandatory && !clerkingObject.allergies}
                                    hideHistory={props.pastAttendances}
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CoreDataTextField
                                    disabled={!props.canEditClerking}
                                    patient={props.patient.patient.__id}
                                    path="core.patient.socialHistory"
                                    instanceId={socialHistoryId}
                                    label={t('Social History')}
                                    variant="outlined"
                                    value={clerkingObject.socialHistory}
                                    onChange={(e) => lazyChangeFunction('socialHistory', e)}
                                    showCoreValue
                                    multiline
                                    agyleRefresh
                                    fullWidth
                                    height={70}
                                    hidePreview
                                    hideHistory={props.pastAttendances}
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} sx={{ mt: 2, pl: 2 }}>
                            {copyObsButtonsAvailable && (
                                <Grid item xs={12}>
                                    <div className={classes.regularButtonsSection}>
                                        <AgyleButton
                                            onClick={copyLastObs}
                                            disabled={!props.patient?.patient?.lastObservation || props.disabled}
                                            variant={'contained'}
                                        >
                                            {t('Copy Last Observation')}
                                        </AgyleButton>
                                        <AgyleButton
                                            onClick={openObsDialog}
                                            disabled={!props.patient?.patient?.lastObservation || props.disabled}
                                            style={{ marginLeft: 10 }}
                                            variant={'contained'}
                                        >
                                            {t('Copy From Observation Table')}
                                        </AgyleButton>
                                    </div>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <CoreDataTextField
                                    patient={props.patient.patient.__id}
                                    path="core.patient.examination"
                                    instanceId={examinationFieldId}
                                    label={t('Clerking Examinations')}
                                    variant="outlined"
                                    multiline
                                    agyleRefresh
                                    fullWidth
                                    disabled={!props.canEditClerking}
                                    value={clerkingObject.examination}
                                    onChange={(e) => lazyChangeFunction('examination', e)}
                                    showCoreValue
                                    height={70}
                                    guideAction={handleAddGuide}
                                    hideHistory={props.pastAttendances}
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>

                            {clerkingReview && (
                                <Grid item xs={12}>
                                    <CoreDataTextField
                                        patient={props.patient.patient.__id}
                                        path="core.patient.clerkingReview"
                                        instanceId={clerkingReviewFieldId}
                                        label={t('Review')}
                                        variant="outlined"
                                        multiline
                                        agyleRefresh
                                        fullWidth
                                        disabled={!props.canEditClerking}
                                        value={clerkingObject.clerkingReview}
                                        onChange={(e) => lazyChangeFunction('clerkingReview', e)}
                                        showCoreValue
                                        height={70}
                                        hideHistory={props.pastAttendances}
                                        noBorder={false}
                                        withNewBackground
                                        size="large"
                                    />
                                </Grid>
                            )}

                            {frailtyAgyleMiniForm && (
                                <Grid item xs={12} sx={{ mb: 2 }}>
                                    <AgyleAccordion
                                        label={t('Frailty - Medication Review')}
                                        expanded={expanded.frailty}
                                        onChange={accordionChangeHandler('frailty')}
                                        formBackground
                                    >
                                        <>
                                            <Grid
                                                container
                                                item
                                                xs={12}
                                                sx={{ mt: 2 }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Grid item xs={isSmall ? 12 : 10}>
                                                    <FormLabel>
                                                        {t("Are any of the patient's current medications contributing to presentation?")}
                                                    </FormLabel>
                                                </Grid>

                                                <Grid item xs={isSmall ? 12 : 2}>
                                                    <AgyleBlockSwitch
                                                        onChange={setMedicationReview}
                                                        value={medicationReview}
                                                        customLabels={{
                                                            yes: t('Yes'),
                                                            no: t('No'),
                                                            empty: t('N/A'),
                                                        }}
                                                        nullState
                                                        style={{
                                                            marginLeft: 10,
                                                        }}
                                                    />
                                                </Grid>

                                                {medicationReview === true && (
                                                    <Grid item xs={12}>
                                                        <FormLabel>
                                                            {t('Which/What medications are contributing to this presentation?')}
                                                        </FormLabel>

                                                        <TextFieldDebounce
                                                            // disabled={isFormDisabled}
                                                            name="medicationReview"
                                                            maxRows={25}
                                                            label={t('Provide details...')}
                                                            multiline
                                                            agyleRefresh
                                                            fullWidth
                                                            onChange={handleChangeTextField}
                                                            value={clerkingObject.medicationReview}
                                                            variant="outlined"
                                                            changeValueTrigger
                                                            noBorder={false}
                                                            withNewBackground
                                                            size="large"
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>

                                            <Grid
                                                container
                                                sx={{ mt: 2 }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Grid item xs={isSmall ? 12 : 10}>
                                                    <FormLabel>
                                                        {t(
                                                            'Are you making any adjustments to the regular medication or starting new regular medication?',
                                                        )}
                                                    </FormLabel>
                                                </Grid>

                                                <Grid item xs={isSmall ? 12 : 2}>
                                                    <AgyleBlockSwitch
                                                        onChange={setMedicationReviewAdjustment}
                                                        value={medicationReviewAdjustment}
                                                        customLabels={{
                                                            yes: t('Yes'),
                                                            no: t('No'),
                                                            empty: t('N/A'),
                                                        }}
                                                        nullState
                                                        style={{
                                                            marginLeft: 10,
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sx={{ mt: 1 }}>
                                                    <TextFieldDebounce
                                                        // disabled={isFormDisabled}
                                                        name="medicationReviewAdjustment"
                                                        maxRows={25}
                                                        label={t('Provide details...')}
                                                        multiline
                                                        agyleRefresh
                                                        fullWidth
                                                        onChange={handleChangeTextField}
                                                        value={clerkingObject.medicationReviewAdjustment}
                                                        variant="outlined"
                                                        changeValueTrigger
                                                        noBorder={false}
                                                        withNewBackground
                                                        size="large"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </>
                                    </AgyleAccordion>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <CoreDataTextField
                                    patient={props.patient.patient.__id}
                                    path="core.patient.workingDiagnosis"
                                    instanceId={diagnosisInformationId}
                                    label={t('Differential Diagnosis / Problem List')}
                                    variant="outlined"
                                    multiline
                                    agyleRefresh
                                    fullWidth
                                    disabled={!props.canEditClerking}
                                    value={clerkingObject.workingDiagnosis}
                                    onChange={(e) => lazyChangeFunction('workingDiagnosis', e)}
                                    showCoreValue
                                    height={70}
                                    hideHistory={props.pastAttendances}
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>

                            {!hideECDSInClerking && (
                                <Grid item xs={12}>
                                    <div>
                                        <ControlDiagnosis
                                            disabled={!props.canEditClerking}
                                            patient={props.patient}
                                            label={t('ECDS Clerking Diagnosis')}
                                            color="primary"
                                            agyleRefresh
                                            value={clerkingObject.diagnosis ? clerkingObject.diagnosis : []}
                                            onChange={(value) => {
                                                const clone = _.cloneDeep(clerkingObject);
                                                clone.diagnosis = value;
                                                setClerkingObject(clone);
                                            }}
                                            admitDate={props.patient?.admission?.startDate}
                                            withBorder
                                        />
                                    </div>
                                </Grid>
                            )}
                        </Grid>

                        <Grid container spacing={2} sx={{ mt: hideECDSInClerking ? 0 : 2, pl: 2 }}>
                            <Grid item xs={12}>
                                <CoreDataTextField
                                    patient={props.patient.patient.__id}
                                    path="core.patient.additionalInformation"
                                    instanceId={managementPlanId}
                                    label={t('Clerking Management Plan')}
                                    variant="outlined"
                                    multiline
                                    agyleRefresh
                                    fullWidth
                                    disabled={!props.canEditClerking}
                                    value={clerkingObject.additionalInformation}
                                    onChange={(e) => lazyChangeFunction('additionalInformation', e)}
                                    showCoreValue
                                    height={70}
                                    hideHistory={props.pastAttendances}
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>

                            {!hideECDSInClerking && (
                                <Grid item xs={12}>
                                    <ControlInvestigations
                                        disabled={!props.canEditClerking}
                                        label={t('ECDS Clerking Investigations')}
                                        value={clerkingObject.investigations}
                                        color={'primary'}
                                        agyleRefresh
                                        onChange={(value) => {
                                            const clone = _.cloneDeep(clerkingObject);
                                            clone.investigations = value;
                                            setClerkingObject(clone);
                                        }}
                                        admitDate={props.patient?.admission?.startDate}
                                        withBorder
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <CoreDataTextField
                                    patient={props.patient.patient.__id}
                                    path="core.patient.investigationsDetails"
                                    instanceId={investingationsResultsId}
                                    label={t('Clerking Investigations Results')}
                                    variant="outlined"
                                    multiline
                                    agyleRefresh
                                    fullWidth
                                    disabled={!props.canEditClerking}
                                    value={clerkingObject.investigationsDetails}
                                    onChange={(e) => lazyChangeFunction('investigationsDetails', e)}
                                    showCoreValue
                                    height={70}
                                    hideHistory={props.pastAttendances}
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>

                            {!hideECDSInClerking && (
                                <Grid item xs={12}>
                                    <ControlTreatment
                                        disabled={!props.canEditClerking}
                                        label={t('ECDS Clerking Treatments')}
                                        value={clerkingObject.treatmentAndManagementPlan}
                                        color={'primary'}
                                        agyleRefresh
                                        onChange={(value) => {
                                            const clone = _.cloneDeep(clerkingObject);
                                            clone.treatmentAndManagementPlan = value;
                                            setClerkingObject(clone);
                                        }}
                                        admitDate={props.patient?.admission?.startDate}
                                        withBorder
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <div className={classes.regularButtonsSection}>
                                    <AgyleButton
                                        onClick={() => dispatch(openPatientForm('seniorReview'))}
                                        style={{ marginTop: 10 }}
                                        variant="contained"
                                    >
                                        {t('Senior Review')}
                                    </AgyleButton>
                                </div>
                            </Grid>
                        </Grid>

                        {props.patientType !== PATIENT_AGE_TYPE.PAEDS && (
                            <Grid item xs={12}>
                                <AgyleAccordion
                                    label={t('Safeguarding')}
                                    expanded={expanded.safeguarding}
                                    onChange={accordionChangeHandler('safeguarding')}
                                    formBackground
                                >
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={5} className={classes.safeguardingSwitch}>
                                            <AgyleBlockSwitch
                                                value={clerkingObject.safeguardingConcerns}
                                                disabled={!props.canEditClerking}
                                                onChange={(value) => {
                                                    setClerkingObject((pr) => ({
                                                        ...pr,
                                                        safeguardingConcerns: value,
                                                        safeguardingConcernsDetails: value ? pr.safeguardingConcernsDetails : '',
                                                        safeguardingConcernsAuthor: pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                                    }));
                                                }}
                                            />

                                            <Typography variant="body1" noWrap={false}>
                                                {t('Safeguarding Concerns')}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={7}>
                                            {clerkingObject.safeguardingConcerns && props.patientType !== PATIENT_AGE_TYPE.PAEDS && (
                                                <div> Please consider completing form Cause for concern (Safeguarding Adults) Referral</div>
                                            )}
                                        </Grid>

                                        {clerkingObject.safeguardingConcerns && (
                                            <Grid item xs={12}>
                                                <div className={classes.dictInput}>
                                                    <Safeguarding
                                                        label={t('Safeguarding')}
                                                        value={clerkingObject?.safeguarding || []}
                                                        limitTags={4}
                                                        agyleRefresh
                                                        color="primary"
                                                        onChange={(event) => {
                                                            setClerkingObject((pr) => ({
                                                                ...pr,
                                                                safeguarding: event,
                                                            }));
                                                        }}
                                                        admitDate={props.patient?.admission?.startDate}
                                                    />
                                                </div>
                                            </Grid>
                                        )}

                                        {props.patientType === PATIENT_AGE_TYPE.OVER75 && clerkingObject.safeguardingConcerns && (
                                            <Grid item xs={12}>
                                                <TextFieldDebounce
                                                    disabled={!props.canEditClerking}
                                                    style={{ width: '100%' }}
                                                    label={t('Concerns & Actions taken')}
                                                    multiline
                                                    agyleRefresh
                                                    onChange={(event) => {
                                                        setClerkingObject((pr) => ({
                                                            ...pr,
                                                            safeguardingConcernsDetails: pr.safeguardingConcerns ? event.target.value : '',
                                                            safeguardingConcernsAuthor:
                                                                pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                                        }));
                                                    }}
                                                    value={clerkingObject.safeguardingConcernsDetails}
                                                    variant="outlined"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                        )}

                                        {props.patientType !== PATIENT_AGE_TYPE.PAEDS && (
                                            <Fragment>
                                                <Grid item xs={12} md={5} className={classes.safeguardingSwitch}>
                                                    <AgyleBlockSwitch
                                                        value={clerkingObject.childProtectionConcerns}
                                                        onChange={(value) => {
                                                            setClerkingObject((pr) => ({
                                                                ...pr,
                                                                childProtectionConcerns: value,
                                                                childConcernsDetails: value ? pr.childConcernsDetails : '',
                                                                childProtectionConcernsAuthor:
                                                                    pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                                            }));
                                                        }}
                                                    />
                                                    <Typography variant="body1" noWrap={false}>
                                                        {t('Child Protection Concerns')}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12} md={7}>
                                                    {clerkingObject.childProtectionConcerns && (
                                                        <TextFieldDebounce
                                                            disabled={!props.canEditClerking}
                                                            style={{ width: '100%' }}
                                                            label={t('Concerns & Actions taken')}
                                                            multiline
                                                            agyleRefresh
                                                            onChange={(event) => {
                                                                setClerkingObject((pr) => ({
                                                                    ...pr,
                                                                    childConcernsDetails: pr.childProtectionConcerns
                                                                        ? event.target.value
                                                                        : '',
                                                                    childProtectionConcernsAuthor:
                                                                        pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                                                }));
                                                            }}
                                                            value={clerkingObject.childConcernsDetails}
                                                            variant="outlined"
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                        />
                                                    )}
                                                </Grid>
                                            </Fragment>
                                        )}
                                    </Grid>
                                </AgyleAccordion>
                            </Grid>
                        )}
                    </>
                )}

                <ObservationsComparison
                    patient={props.patient}
                    open={isOpenObsDialog}
                    onClose={closeObsDialog}
                    chosenObs={chosenObs}
                    setChosenObs={setChosenObs}
                    setDataObject={setClerkingObject}
                />
            </Grid>
        )
    );
};

export { Clerking };
